import { UnregisterCallback } from "history";
import { inject } from "mobx-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import type { Store } from "@inferno/renderer-shared-core";

import { isPWSSessionBackend, PopupState } from "@inferno/renderer-shared-core";
import { Modal } from "../../../../core/ui";
import { CoastPopUpMessageModal } from "./CoastPopUpMessageModal.component";
import "./CoastUserModal.style.scss";
import { CoastLoginModal } from "./login/CoastLoginModal.component";
import { CoastUserSettingsLoader } from "./user-settings/CoastUserSettingsLoader.component";

interface CoastUserModalProps {
  store?: Store;
}

const SUPPORTED_VIEWS = new Set(["login", "message", "account"]);

const CoastUserModal = inject("store")(({ store }: CoastUserModalProps) => {
  const historyUnsubscribe = useRef<UnregisterCallback>();
  const currentSession = store?.session?.currentSession;
  const history = useHistory();

  if (!isPWSSessionBackend(currentSession)) {
    return null;
  }

  const [currentView, setCurrentView] = useState<PopupState>(currentSession.userModalState || { view: null });

  const handlePopupStateChange = useCallback((state: PopupState) => setCurrentView(state), []);

  useEffect(() => {
    historyUnsubscribe.current = history.listen(currentSession.onNavigate);
    currentSession.onUserModalStageChanged.subscribe(handlePopupStateChange);

    return () => {
      currentSession.onUserModalStageChanged.unsubscribe(handlePopupStateChange);
      historyUnsubscribe.current?.();
    };
  }, [currentSession.onNavigate, currentSession.onUserModalStageChanged, handlePopupStateChange, history]);

  if (currentView.view && !SUPPORTED_VIEWS.has(currentView.view)) {
    return null;
  }

  return (
    <Modal
      addClass={`coast-user-modal light-bg ${currentView.view}`}
      callback={() => currentSession.hideUsersPopup()}
      open={!!currentView.view}
    >
      {currentView.view === "login" ? <CoastLoginModal store={store} viewData={currentView.data} /> : null}
      {currentView.view === "message" && currentView.data?.message ? (
        <CoastPopUpMessageModal message={currentView.data.message} />
      ) : null}
      {currentView.view === "account" ? <CoastUserSettingsLoader viewData={currentView.data} /> : null}
    </Modal>
  );
});

export default CoastUserModal;
